/**
 * ASM-SPEC-01 §7 — `<CreatableCombobox>`: suggestions that never restrict.
 *
 * The freeSolo `Autocomplete<string, false, false, true>` dance that
 * `apps/web` copies inline in seven places (the Manufacturer field in
 * `AddTurbineModelDialog`, the Sites Country field, …), made once. Two
 * properties are load-bearing and are the reason this is a primitive rather
 * than another copy:
 *
 * 1. **Free text survives.** Anything typed that matches no option stays as
 *    typed — on blur, on `Enter`, on `Tab`. The vocabulary this fronts is
 *    open by decision (059 §9.5 / ASM-SPEC-01 D-2); a combobox that silently
 *    reverted to the last valid option would quietly enforce a closed one.
 * 2. **Picking is distinguishable from typing.** `onChange` fires for every
 *    keystroke AND for a pick, so the caller can bind it to a form field;
 *    `onOptionSelected` fires ONLY for a pick, so the caller can react to
 *    the option's own data. The Specifications editor needs exactly this:
 *    choosing a catalogue parameter fills the row's unit (D-5), typing one
 *    never touches it.
 *
 * `readOnly` mirrors `AssetTypeInlineSelect` in `EditTurbineModelDialog`:
 * MUI's `Autocomplete readOnly` suppresses the popper and the clear button
 * while leaving the input focusable and readable — the exact behaviour the
 * View-mode modal needs, and the regression the note there warns about.
 */
import type { ReactElement } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";

export interface CreatableComboboxOption {
  /** The value itself — the option's primary text, and what is committed. */
  value: string;
  /** Muted, right-aligned secondary text. Participates in filtering. */
  secondary?: string;
}

export interface CreatableComboboxProps {
  value: string;
  /** Fires on every keystroke AND on an option pick. */
  onChange: (next: string) => void;
  /** Fires ONLY when an option is picked from the list. */
  onOptionSelected?: (option: CreatableComboboxOption) => void;
  options: readonly CreatableComboboxOption[];
  /** Rendered verbatim on the `<input>` — tests query by it. */
  ariaLabel: string;
  placeholder?: string;
  /** No popper, no clear button, input read-only — mirrors AssetTypeInlineSelect. */
  readOnly?: boolean;
  width?: number | string;
  size?: "small" | "medium";
}

/** Substring, case-insensitive, over the value AND the secondary text. */
function matches(option: CreatableComboboxOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  return (
    option.value.toLowerCase().includes(q) ||
    (option.secondary ?? "").toLowerCase().includes(q)
  );
}

export function CreatableCombobox({
  value,
  onChange,
  onOptionSelected,
  options,
  ariaLabel,
  placeholder,
  readOnly = false,
  width,
  size = "small",
}: CreatableComboboxProps): ReactElement {
  return (
    <Autocomplete<CreatableComboboxOption, false, false, true>
      freeSolo
      selectOnFocus={false}
      handleHomeEndKeys
      // `readOnly` alone: MUI suppresses the popper AND the clear indicator
      // for it. `disableClearable` is NOT used — it is a generic parameter as
      // well as a prop, so a dynamic value would change the component's own
      // value type. `open={false}` is likewise avoided: it would fight MUI's
      // control of the popper.
      readOnly={readOnly}
      options={[...options]}
      size={size}
      // The input is the source of truth, not a selected option: this is a
      // free-text field that happens to offer suggestions. Passing the string
      // as `value` keeps MUI from clearing it when it matches no option.
      value={value}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.value)}
      isOptionEqualToValue={(o, v) =>
        o.value === (typeof v === "string" ? v : v.value)
      }
      filterOptions={(opts, state) =>
        opts.filter((o) => matches(o, state.inputValue))
      }
      onInputChange={(_e, next, reason) => {
        // ONLY the user's own edits. MUI also fires this with
        // `reason: "reset"` when the `value` prop changes from outside —
        // including once on mount, and again after a pick — and propagating
        // that would let the combobox overwrite the form field it is being
        // driven by. A pick is reported through `onChange` below; a clear
        // comes through here as `"clear"`.
        if (reason !== "input" && reason !== "clear") return;
        onChange(next);
      }}
      onChange={(_e, next) => {
        if (next === null) {
          onChange("");
          return;
        }
        if (typeof next === "string") {
          // Free text committed with Enter — kept exactly as typed.
          onChange(next);
          return;
        }
        onChange(next.value);
        onOptionSelected?.(next);
      }}
      renderOption={(props, option) => (
        // Spread first, then the key — the house pattern (ContactPicker,
        // AiModelMultiSelect). MUI v6 puts a `key` inside `props`, and the
        // option's own value is the stable one.
        <Box
          component="li"
          {...props}
          key={option.value}
          sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}
        >
          <Box component="span">{option.value}</Box>
          {option.secondary !== undefined && option.secondary !== "" ? (
            <Box
              component="span"
              sx={{ color: "text.secondary", fontSize: "0.8125rem" }}
            >
              {option.secondary}
            </Box>
          ) : null}
        </Box>
      )}
      {...(width === undefined ? {} : { sx: { width } })}
      renderInput={(params) => {
        // exactOptionalPropertyTypes friction — the same pass-through dance
        // every Autocomplete call site in this repo uses.
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            size={size}
            {...(placeholder === undefined ? {} : { placeholder })}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": ariaLabel,
                ...(readOnly ? { readOnly: true } : {}),
              },
            }}
          />
        );
        /* eslint-enable */
      }}
    />
  );
}
